/**
 * AST Interpreter
 * Executes validated AST in a controlled environment
 */

import { RuntimeError, IterationLimitError } from './errors.js'

// Return value sentinel
const RETURN_VALUE = Symbol('RETURN_VALUE')
const BREAK_SIGNAL = Symbol('BREAK_SIGNAL')
const CONTINUE_SIGNAL = Symbol('CONTINUE_SIGNAL')

/**
 * Scope for variable bindings
 */
class Scope {
  constructor(parent = null) {
    this.parent = parent
    this.bindings = new Map()
  }

  define(name, value, kind = 'let') {
    if (this.bindings.has(name) && kind !== 'var') {
      throw new RuntimeError(`Identifier '${name}' has already been declared`)
    }
    this.bindings.set(name, { value, kind, initialized: true })
  }

  get(name) {
    if (this.bindings.has(name)) {
      const binding = this.bindings.get(name)
      if (!binding.initialized) {
        throw new RuntimeError(`Cannot access '${name}' before initialization`)
      }
      return binding.value
    }
    if (this.parent) {
      return this.parent.get(name)
    }
    throw new ReferenceError(`${name} is not defined`)
  }

  set(name, value) {
    if (this.bindings.has(name)) {
      const binding = this.bindings.get(name)
      if (binding.kind === 'const') {
        throw new TypeError(`Assignment to constant variable '${name}'`)
      }
      binding.value = value
      return
    }
    if (this.parent) {
      return this.parent.set(name, value)
    }
    throw new ReferenceError(`${name} is not defined`)
  }

  has(name) {
    if (this.bindings.has(name)) return true
    if (this.parent) return this.parent.has(name)
    return false
  }
}

/**
 * Execution context
 */
class ExecutionContext {
  constructor(options = {}) {
    this.iterations = 0
    this.maxIterations = options.maxIterations || 10000
    this.startTime = Date.now()
    this.timeout = options.timeout || 30000
  }

  checkLimits() {
    this.iterations++
    if (this.iterations > this.maxIterations) {
      throw new IterationLimitError(this.maxIterations)
    }
    // Timeout is handled externally via Promise.race
  }
}

/**
 * Create interpreter with globals
 */
export function createInterpreter(globals) {
  const globalScope = new Scope()

  // Inject globals
  for (const [name, value] of Object.entries(globals)) {
    globalScope.define(name, value, 'const')
  }

  return {
    async execute(ast, options = {}) {
      const ctx = new ExecutionContext(options)
      const scope = new Scope(globalScope)

      try {
        const result = await evaluate(ast, scope, ctx)
        return result
      } catch (error) {
        if (error[RETURN_VALUE] !== undefined) {
          return error[RETURN_VALUE]
        }
        throw error
      }
    },
  }
}

/**
 * Evaluate AST node
 */
async function evaluate(node, scope, ctx) {
  if (!node) return undefined
  ctx.checkLimits()

  switch (node.type) {
    case 'Program':
      return evaluateProgram(node, scope, ctx)

    case 'BlockStatement':
      return evaluateBlock(node, scope, ctx)

    case 'ExpressionStatement':
      return evaluate(node.expression, scope, ctx)

    case 'VariableDeclaration':
      return evaluateVariableDeclaration(node, scope, ctx)

    case 'FunctionDeclaration':
      return evaluateFunctionDeclaration(node, scope, ctx)

    case 'ReturnStatement':
      return evaluateReturn(node, scope, ctx)

    case 'IfStatement':
      return evaluateIf(node, scope, ctx)

    case 'SwitchStatement':
      return evaluateSwitch(node, scope, ctx)

    case 'ForStatement':
      return evaluateFor(node, scope, ctx)

    case 'ForOfStatement':
      return evaluateForOf(node, scope, ctx)

    case 'ForInStatement':
      return evaluateForIn(node, scope, ctx)

    case 'WhileStatement':
      return evaluateWhile(node, scope, ctx)

    case 'DoWhileStatement':
      return evaluateDoWhile(node, scope, ctx)

    case 'BreakStatement':
      throw { [BREAK_SIGNAL]: true }

    case 'ContinueStatement':
      throw { [CONTINUE_SIGNAL]: true }

    case 'ThrowStatement':
      throw await evaluate(node.argument, scope, ctx)

    case 'TryStatement':
      return evaluateTry(node, scope, ctx)

    case 'Identifier':
      return scope.get(node.name)

    case 'Literal':
      return node.value

    case 'TemplateLiteral':
      return evaluateTemplateLiteral(node, scope, ctx)

    case 'ArrayExpression':
      return evaluateArray(node, scope, ctx)

    case 'ObjectExpression':
      return evaluateObject(node, scope, ctx)

    case 'FunctionExpression':
    case 'ArrowFunctionExpression':
      return createFunction(node, scope, ctx)

    case 'CallExpression':
      return evaluateCall(node, scope, ctx)

    case 'NewExpression':
      return evaluateNew(node, scope, ctx)

    case 'MemberExpression':
      return evaluateMember(node, scope, ctx)

    case 'EmptyStatement':
      return undefined

    case 'AssignmentExpression':
      return evaluateAssignment(node, scope, ctx)

    case 'UpdateExpression':
      return evaluateUpdate(node, scope, ctx)

    case 'UnaryExpression':
      return evaluateUnary(node, scope, ctx)

    case 'BinaryExpression':
      return evaluateBinary(node, scope, ctx)

    case 'LogicalExpression':
      return evaluateLogical(node, scope, ctx)

    case 'ConditionalExpression':
      return evaluateConditional(node, scope, ctx)

    case 'SequenceExpression':
      return evaluateSequence(node, scope, ctx)

    case 'AwaitExpression':
      return await evaluate(node.argument, scope, ctx)

    case 'SpreadElement':
      return { spread: await evaluate(node.argument, scope, ctx) }

    default:
      throw new RuntimeError(`Unsupported node type: ${node.type}`)
  }
}

async function evaluateProgram(node, scope, ctx) {
  let result
  for (const stmt of node.body) {
    try {
      result = await evaluate(stmt, scope, ctx)
    } catch (e) {
      if (e[RETURN_VALUE] !== undefined) {
        return e[RETURN_VALUE]
      }
      throw e
    }
  }
  return result
}

async function evaluateBlock(node, scope, ctx) {
  const blockScope = new Scope(scope)
  let result
  for (const stmt of node.body) {
    result = await evaluate(stmt, blockScope, ctx)
  }
  return result
}

async function evaluateVariableDeclaration(node, scope, ctx) {
  const kind = node.kind // 'var' | 'let' | 'const'
  for (const decl of node.declarations) {
    const value = decl.init ? await evaluate(decl.init, scope, ctx) : undefined
    if (decl.id.type === 'Identifier') {
      scope.define(decl.id.name, value, kind)
    } else {
      // Destructuring
      await assignPattern(decl.id, value, scope, ctx, kind)
    }
  }
}

async function evaluateFunctionDeclaration(node, scope, ctx) {
  const fn = createFunction(node, scope, ctx)
  scope.define(node.id.name, fn, 'var')
}

function createFunction(node, scope, ctx) {
  const fn = async function (...args) {
    const fnScope = new Scope(scope)

    // Bind parameters
    for (let i = 0; i < node.params.length; i++) {
      const param = node.params[i]
      if (param.type === 'Identifier') {
        fnScope.define(param.name, args[i], 'let')
      } else if (param.type === 'RestElement') {
        fnScope.define(param.argument.name, args.slice(i), 'let')
      } else if (param.type === 'AssignmentPattern') {
        const value = args[i] !== undefined ? args[i] : await evaluate(param.right, fnScope, ctx)
        fnScope.define(param.left.name, value, 'let')
      } else {
        await assignPattern(param, args[i], fnScope, ctx, 'let')
      }
    }

    try {
      if (node.body.type === 'BlockStatement') {
        await evaluate(node.body, fnScope, ctx)
        return undefined
      } else {
        // Arrow function with expression body
        return await evaluate(node.body, fnScope, ctx)
      }
    } catch (e) {
      if (e[RETURN_VALUE] !== undefined) {
        return e[RETURN_VALUE]
      }
      throw e
    }
  }

  return fn
}

async function evaluateReturn(node, scope, ctx) {
  const value = node.argument ? await evaluate(node.argument, scope, ctx) : undefined
  throw { [RETURN_VALUE]: value }
}

async function evaluateIf(node, scope, ctx) {
  const test = await evaluate(node.test, scope, ctx)
  if (test) {
    return evaluate(node.consequent, scope, ctx)
  } else if (node.alternate) {
    return evaluate(node.alternate, scope, ctx)
  }
}

async function evaluateSwitch(node, scope, ctx) {
  const discriminant = await evaluate(node.discriminant, scope, ctx)
  let matched = false

  for (const caseNode of node.cases) {
    if (!matched) {
      if (caseNode.test === null) {
        // default case
        matched = true
      } else {
        const test = await evaluate(caseNode.test, scope, ctx)
        if (discriminant === test) {
          matched = true
        }
      }
    }

    if (matched) {
      try {
        for (const stmt of caseNode.consequent) {
          await evaluate(stmt, scope, ctx)
        }
      } catch (e) {
        if (e[BREAK_SIGNAL]) break
        throw e
      }
    }
  }
}

async function evaluateFor(node, scope, ctx) {
  const loopScope = new Scope(scope)

  if (node.init) {
    await evaluate(node.init, loopScope, ctx)
  }

  while (true) {
    if (node.test) {
      const test = await evaluate(node.test, loopScope, ctx)
      if (!test) break
    }

    try {
      await evaluate(node.body, loopScope, ctx)
    } catch (e) {
      if (e[BREAK_SIGNAL]) break
      if (e[CONTINUE_SIGNAL]) {
        // continue
      } else {
        throw e
      }
    }

    if (node.update) {
      await evaluate(node.update, loopScope, ctx)
    }
  }
}

async function evaluateForOf(node, scope, ctx) {
  const iterable = await evaluate(node.right, scope, ctx)

  for (const item of iterable) {
    const loopScope = new Scope(scope)

    if (node.left.type === 'VariableDeclaration') {
      const decl = node.left.declarations[0]
      if (decl.id.type === 'Identifier') {
        loopScope.define(decl.id.name, item, node.left.kind)
      } else {
        await assignPattern(decl.id, item, loopScope, ctx, node.left.kind)
      }
    } else {
      await evaluateAssignmentTarget(node.left, item, loopScope, ctx)
    }

    try {
      await evaluate(node.body, loopScope, ctx)
    } catch (e) {
      if (e[BREAK_SIGNAL]) break
      if (e[CONTINUE_SIGNAL]) continue
      throw e
    }
  }
}

async function evaluateForIn(node, scope, ctx) {
  const obj = await evaluate(node.right, scope, ctx)

  for (const key in obj) {
    const loopScope = new Scope(scope)

    if (node.left.type === 'VariableDeclaration') {
      const decl = node.left.declarations[0]
      loopScope.define(decl.id.name, key, node.left.kind)
    } else {
      await evaluateAssignmentTarget(node.left, key, loopScope, ctx)
    }

    try {
      await evaluate(node.body, loopScope, ctx)
    } catch (e) {
      if (e[BREAK_SIGNAL]) break
      if (e[CONTINUE_SIGNAL]) continue
      throw e
    }
  }
}

async function evaluateWhile(node, scope, ctx) {
  while (await evaluate(node.test, scope, ctx)) {
    try {
      await evaluate(node.body, scope, ctx)
    } catch (e) {
      if (e[BREAK_SIGNAL]) break
      if (e[CONTINUE_SIGNAL]) continue
      throw e
    }
  }
}

async function evaluateDoWhile(node, scope, ctx) {
  do {
    try {
      await evaluate(node.body, scope, ctx)
    } catch (e) {
      if (e[BREAK_SIGNAL]) break
      if (e[CONTINUE_SIGNAL]) continue
      throw e
    }
  } while (await evaluate(node.test, scope, ctx))
}

async function evaluateTry(node, scope, ctx) {
  try {
    return await evaluate(node.block, scope, ctx)
  } catch (error) {
    if (error[RETURN_VALUE] !== undefined || error[BREAK_SIGNAL] || error[CONTINUE_SIGNAL]) {
      throw error
    }
    if (node.handler) {
      const catchScope = new Scope(scope)
      if (node.handler.param) {
        catchScope.define(node.handler.param.name, error, 'let')
      }
      return await evaluate(node.handler.body, catchScope, ctx)
    }
    throw error
  } finally {
    if (node.finalizer) {
      await evaluate(node.finalizer, scope, ctx)
    }
  }
}

async function evaluateTemplateLiteral(node, scope, ctx) {
  let result = ''
  for (let i = 0; i < node.quasis.length; i++) {
    result += node.quasis[i].value.cooked
    if (i < node.expressions.length) {
      result += String(await evaluate(node.expressions[i], scope, ctx))
    }
  }
  return result
}

async function evaluateArray(node, scope, ctx) {
  const result = []
  for (const elem of node.elements) {
    if (elem === null) {
      result.push(undefined)
    } else if (elem.type === 'SpreadElement') {
      const spread = await evaluate(elem.argument, scope, ctx)
      result.push(...spread)
    } else {
      result.push(await evaluate(elem, scope, ctx))
    }
  }
  return result
}

async function evaluateObject(node, scope, ctx) {
  const result = {}
  for (const prop of node.properties) {
    if (prop.type === 'SpreadElement') {
      const spread = await evaluate(prop.argument, scope, ctx)
      Object.assign(result, spread)
    } else {
      const key = prop.computed
        ? await evaluate(prop.key, scope, ctx)
        : prop.key.name || prop.key.value
      const value = await evaluate(prop.value, scope, ctx)
      result[key] = value
    }
  }
  return result
}

async function evaluateCall(node, scope, ctx) {
  let callee, thisArg

  if (node.callee.type === 'MemberExpression') {
    thisArg = await evaluate(node.callee.object, scope, ctx)
    const prop = node.callee.computed
      ? await evaluate(node.callee.property, scope, ctx)
      : node.callee.property.name
    callee = thisArg[prop]
  } else {
    callee = await evaluate(node.callee, scope, ctx)
    thisArg = undefined
  }

  if (typeof callee !== 'function') {
    throw new TypeError(`${callee} is not a function`)
  }

  const args = []
  for (const arg of node.arguments) {
    if (arg.type === 'SpreadElement') {
      const spread = await evaluate(arg.argument, scope, ctx)
      args.push(...spread)
    } else {
      args.push(await evaluate(arg, scope, ctx))
    }
  }

  return await callee.apply(thisArg, args)
}

async function evaluateNew(node, scope, ctx) {
  const callee = await evaluate(node.callee, scope, ctx)

  if (typeof callee !== 'function') {
    throw new TypeError(`${callee} is not a constructor`)
  }

  const args = []
  for (const arg of node.arguments) {
    if (arg.type === 'SpreadElement') {
      const spread = await evaluate(arg.argument, scope, ctx)
      args.push(...spread)
    } else {
      args.push(await evaluate(arg, scope, ctx))
    }
  }

  return new callee(...args)
}

async function evaluateMember(node, scope, ctx) {
  const obj = await evaluate(node.object, scope, ctx)
  const prop = node.computed
    ? await evaluate(node.property, scope, ctx)
    : node.property.name

  return obj[prop]
}

async function evaluateAssignment(node, scope, ctx) {
  const value = await evaluate(node.right, scope, ctx)

  if (node.operator !== '=') {
    const left = await evaluateLValue(node.left, scope, ctx)
    const newValue = applyAssignmentOp(node.operator, left, value)
    await evaluateAssignmentTarget(node.left, newValue, scope, ctx)
    return newValue
  }

  await evaluateAssignmentTarget(node.left, value, scope, ctx)
  return value
}

async function evaluateLValue(node, scope, ctx) {
  if (node.type === 'Identifier') {
    return scope.get(node.name)
  } else if (node.type === 'MemberExpression') {
    return evaluateMember(node, scope, ctx)
  }
  throw new RuntimeError('Invalid left-hand side in assignment')
}

async function evaluateAssignmentTarget(node, value, scope, ctx) {
  if (node.type === 'Identifier') {
    scope.set(node.name, value)
  } else if (node.type === 'MemberExpression') {
    const obj = await evaluate(node.object, scope, ctx)
    const prop = node.computed
      ? await evaluate(node.property, scope, ctx)
      : node.property.name
    obj[prop] = value
  } else if (node.type === 'ArrayPattern' || node.type === 'ObjectPattern') {
    await assignPattern(node, value, scope, ctx, 'let')
  } else {
    throw new RuntimeError('Invalid assignment target')
  }
}

async function assignPattern(pattern, value, scope, ctx, kind) {
  if (pattern.type === 'ArrayPattern') {
    for (let i = 0; i < pattern.elements.length; i++) {
      const elem = pattern.elements[i]
      if (!elem) continue
      if (elem.type === 'RestElement') {
        scope.define(elem.argument.name, value.slice(i), kind)
      } else if (elem.type === 'Identifier') {
        scope.define(elem.name, value[i], kind)
      } else if (elem.type === 'AssignmentPattern') {
        const val = value[i] !== undefined ? value[i] : await evaluate(elem.right, scope, ctx)
        scope.define(elem.left.name, val, kind)
      } else {
        await assignPattern(elem, value[i], scope, ctx, kind)
      }
    }
  } else if (pattern.type === 'ObjectPattern') {
    for (const prop of pattern.properties) {
      if (prop.type === 'RestElement') {
        const rest = { ...value }
        for (const p of pattern.properties) {
          if (p !== prop && p.key) {
            delete rest[p.key.name || p.key.value]
          }
        }
        scope.define(prop.argument.name, rest, kind)
      } else {
        const key = prop.key.name || prop.key.value
        if (prop.value.type === 'Identifier') {
          scope.define(prop.value.name, value[key], kind)
        } else if (prop.value.type === 'AssignmentPattern') {
          const val =
            value[key] !== undefined ? value[key] : await evaluate(prop.value.right, scope, ctx)
          scope.define(prop.value.left.name, val, kind)
        } else {
          await assignPattern(prop.value, value[key], scope, ctx, kind)
        }
      }
    }
  }
}

async function evaluateUpdate(node, scope, ctx) {
  const oldValue = await evaluateLValue(node.argument, scope, ctx)
  const newValue = node.operator === '++' ? oldValue + 1 : oldValue - 1
  await evaluateAssignmentTarget(node.argument, newValue, scope, ctx)
  return node.prefix ? newValue : oldValue
}

async function evaluateUnary(node, scope, ctx) {
  const arg = await evaluate(node.argument, scope, ctx)
  switch (node.operator) {
    case '-':
      return -arg
    case '+':
      return +arg
    case '!':
      return !arg
    case '~':
      return ~arg
    case 'typeof':
      return typeof arg
    case 'void':
      return undefined
    default:
      throw new RuntimeError(`Unknown unary operator: ${node.operator}`)
  }
}

async function evaluateBinary(node, scope, ctx) {
  const left = await evaluate(node.left, scope, ctx)
  const right = await evaluate(node.right, scope, ctx)
  return applyBinaryOp(node.operator, left, right)
}

function applyBinaryOp(op, left, right) {
  switch (op) {
    case '+':
      return left + right
    case '-':
      return left - right
    case '*':
      return left * right
    case '/':
      return left / right
    case '%':
      return left % right
    case '**':
      return left ** right
    case '==':
      return left == right
    case '!=':
      return left != right
    case '===':
      return left === right
    case '!==':
      return left !== right
    case '<':
      return left < right
    case '<=':
      return left <= right
    case '>':
      return left > right
    case '>=':
      return left >= right
    case '&':
      return left & right
    case '|':
      return left | right
    case '^':
      return left ^ right
    case '<<':
      return left << right
    case '>>':
      return left >> right
    case '>>>':
      return left >>> right
    case 'in':
      return left in right
    case 'instanceof':
      return left instanceof right
    default:
      throw new RuntimeError(`Unknown binary operator: ${op}`)
  }
}

function applyAssignmentOp(op, left, right) {
  switch (op) {
    case '+=':
      return left + right
    case '-=':
      return left - right
    case '*=':
      return left * right
    case '/=':
      return left / right
    case '%=':
      return left % right
    case '**=':
      return left ** right
    case '&=':
      return left & right
    case '|=':
      return left | right
    case '^=':
      return left ^ right
    case '<<=':
      return left << right
    case '>>=':
      return left >> right
    case '>>>=':
      return left >>> right
    default:
      throw new RuntimeError(`Unknown assignment operator: ${op}`)
  }
}

async function evaluateLogical(node, scope, ctx) {
  const left = await evaluate(node.left, scope, ctx)
  switch (node.operator) {
    case '&&':
      return left ? await evaluate(node.right, scope, ctx) : left
    case '||':
      return left ? left : await evaluate(node.right, scope, ctx)
    case '??':
      return left != null ? left : await evaluate(node.right, scope, ctx)
    default:
      throw new RuntimeError(`Unknown logical operator: ${node.operator}`)
  }
}

async function evaluateConditional(node, scope, ctx) {
  const test = await evaluate(node.test, scope, ctx)
  return test ? evaluate(node.consequent, scope, ctx) : evaluate(node.alternate, scope, ctx)
}

async function evaluateSequence(node, scope, ctx) {
  let result
  for (const expr of node.expressions) {
    result = await evaluate(expr, scope, ctx)
  }
  return result
}
